import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { type KeyboardEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useUpdateCurrentUserPassword } from '~entities/users/api/update-current-user-password';

import { useIsMobile } from '~shared/shadcn/hooks/use-mobile';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';

import {
  buildMobileDialogViewportStyle,
  focusNextPasswordField,
  getNextPasswordFieldId,
  scrollPasswordFieldIntoView,
} from './update-password-dialog.helpers';
import {
  UPDATE_PASSWORD_FIELD,
  type MobileDialogViewportStyle,
  type UpdatePasswordFieldId,
} from './update-password-dialog.types';

interface UpdatePasswordDialogProps {
  open: boolean;
  validateCurrentPassword: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
}

const validatePassword = (password: string) => {
  const requirements = {
    uppercase: /[A-Z]/.test(password),
    digit: /\d/.test(password),
    symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    minLength: password.length >= 6,
  };
  return requirements;
};

export const UpdatePasswordDialog = ({
  open,
  onOpenChange,
  validateCurrentPassword = true,
  onClose,
}: UpdatePasswordDialogProps) => {
  const updatePasswordSchema = z
    .object({
      currentPassword: validateCurrentPassword
        ? z.string().min(1, 'Current password is required')
        : z.string().optional(),
      newPassword: z
        .string()
        .min(6, 'Password must be at least 6 characters')
        .refine(
          (password) => /[A-Z]/.test(password),
          'Password must contain at least one uppercase letter',
        )
        .refine(
          (password) => /\d/.test(password),
          'Password must contain at least one number',
        )
        .refine(
          (password) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
          'Password must contain at least one special character',
        ),
      confirmPassword: z.string(),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: "Passwords don't match",
      path: ['confirmPassword'],
    })
    .refine((data) => data.currentPassword !== data.newPassword, {
      message: 'New password must be different from current password',
      path: ['newPassword'],
    });

  type UpdatePasswordFormValues = z.infer<typeof updatePasswordSchema>;

  const updatePasswordMutation = useUpdateCurrentUserPassword();

  const updatePasswordForm = useForm<UpdatePasswordFormValues>({
    resolver: zodResolver(updatePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const watchNewPassword = updatePasswordForm.watch('newPassword');
  const [requirements, setRequirements] = useState(
    validatePassword(watchNewPassword),
  );
  const isMobile = useIsMobile();
  const [mobileViewportStyle, setMobileViewportStyle] = useState<
    MobileDialogViewportStyle | undefined
  >();

  useEffect(() => {
    setRequirements(validatePassword(watchNewPassword));
  }, [watchNewPassword]);

  useEffect(() => {
    if (!open || !isMobile) {
      setMobileViewportStyle(undefined);
      return;
    }

    const updateViewportStyle = () => {
      try {
        const visualViewport = window.visualViewport;
        setMobileViewportStyle(
          buildMobileDialogViewportStyle(
            8,
            visualViewport
              ? {
                  offsetTop: visualViewport.offsetTop,
                  height: visualViewport.height,
                }
              : undefined,
            window.innerHeight,
          ),
        );
      } catch (error) {
        console.error('Failed to keep the password dialog above the keyboard', {
          error,
        });
      }
    };

    updateViewportStyle();
    window.visualViewport?.addEventListener('resize', updateViewportStyle);
    window.visualViewport?.addEventListener('scroll', updateViewportStyle);

    return () => {
      window.visualViewport?.removeEventListener('resize', updateViewportStyle);
      window.visualViewport?.removeEventListener('scroll', updateViewportStyle);
    };
  }, [isMobile, open]);

  const handlePasswordFieldKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    currentFieldId: UpdatePasswordFieldId,
  ) => {
    if (event.key !== 'Enter' || !getNextPasswordFieldId(currentFieldId)) {
      return;
    }

    event.preventDefault();
    focusNextPasswordField(currentFieldId);
  };

  const handleClose = (allowClose = false) => {
    // If user doesn't have a password, prevent closing unless explicitly allowed
    // (e.g., after successful password update)
    if (!validateCurrentPassword && !allowClose) {
      toast.error('You must set a password before you can close this dialog');
      return;
    }
    updatePasswordForm.reset();
    onClose();
  };

  const handleOpenChange = (newOpen: boolean) => {
    // If trying to close and user doesn't have a password, prevent it
    if (!newOpen && !validateCurrentPassword) {
      toast.error('You must set a password before you can close this dialog');
      return;
    }
    onOpenChange(newOpen);
  };

  const onSubmit = async (values: UpdatePasswordFormValues) => {
    try {
      await updatePasswordMutation.mutateAsync({
        currentPassword: values.currentPassword ?? '',
        newPassword: values.newPassword,
      });
      // Allow closing after successful password update
      handleClose(true);
    } catch (error) {
      // Error is handled by the mutation's onError callback
      console.error(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className='flex max-h-[calc(100svh-2rem)] flex-col overflow-y-auto overscroll-contain'
        style={mobileViewportStyle}
        onInteractOutside={(e) => {
          if (!validateCurrentPassword) {
            e.preventDefault();
            toast.error(
              'You must set a password before you can close this dialog',
            );
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!validateCurrentPassword) {
            e.preventDefault();
            toast.error(
              'You must set a password before you can close this dialog',
            );
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Update Password</DialogTitle>
          <DialogDescription>
            Choose a new password for your account.
          </DialogDescription>
        </DialogHeader>
        <Form {...updatePasswordForm}>
          <form
            onSubmit={updatePasswordForm.handleSubmit(onSubmit)}
            className='space-y-4'
          >
            {validateCurrentPassword && (
              <FormField
                control={updatePasswordForm.control}
                name='currentPassword'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Password</FormLabel>
                    <FormControl>
                      <Input
                        id={UPDATE_PASSWORD_FIELD.CURRENT}
                        type='password'
                        autoComplete='current-password'
                        enterKeyHint='next'
                        placeholder='Enter current password'
                        className='text-base'
                        {...field}
                        onFocus={(event) => {
                          field.onFocus?.(event);
                          scrollPasswordFieldIntoView(event.currentTarget);
                        }}
                        onKeyDown={(event) => {
                          handlePasswordFieldKeyDown(
                            event,
                            UPDATE_PASSWORD_FIELD.CURRENT,
                          );
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={updatePasswordForm.control}
              name='newPassword'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <Input
                      id={UPDATE_PASSWORD_FIELD.NEW}
                      type='password'
                      autoComplete='new-password'
                      enterKeyHint='next'
                      placeholder='Enter new password'
                      className='text-base'
                      {...field}
                      onFocus={(event) => {
                        field.onFocus?.(event);
                        scrollPasswordFieldIntoView(event.currentTarget);
                      }}
                      onKeyDown={(event) => {
                        handlePasswordFieldKeyDown(
                          event,
                          UPDATE_PASSWORD_FIELD.NEW,
                        );
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className='mt-2 space-y-1 text-sm'>
              <p
                className={
                  requirements.minLength ? 'text-green-600' : 'text-gray-500'
                }
              >
                • Minimum 6 characters
              </p>
              <p
                className={
                  requirements.uppercase ? 'text-green-600' : 'text-gray-500'
                }
              >
                • At least one uppercase letter
              </p>
              <p
                className={
                  requirements.digit ? 'text-green-600' : 'text-gray-500'
                }
              >
                • At least one number
              </p>
              <p
                className={
                  requirements.symbol ? 'text-green-600' : 'text-gray-500'
                }
              >
                {`• At least one special character (!@#$%^&*(),.?":{|})<>`}
              </p>
            </div>
            <FormField
              control={updatePasswordForm.control}
              name='confirmPassword'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm New Password</FormLabel>
                  <FormControl>
                    <Input
                      id={UPDATE_PASSWORD_FIELD.CONFIRM}
                      type='password'
                      autoComplete='new-password'
                      enterKeyHint='done'
                      placeholder='Confirm new password'
                      className='text-base'
                      {...field}
                      onFocus={(event) => {
                        field.onFocus?.(event);
                        scrollPasswordFieldIntoView(event.currentTarget);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  handleClose();
                }}
                disabled={updatePasswordMutation.isPending}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={updatePasswordMutation.isPending}>
                {updatePasswordMutation.isPending ? (
                  <>
                    <Loader2 className='mr-2 size-4 animate-spin' />
                    Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
